import { morph, MORPH_OPTIONS } from '@theme/morph';
import { normalizeSectionId } from '@theme/section-renderer';

const SELECTORS = {
  mainContent: '#MainContent',
  switcher: '[data-collection-switcher]',
  link: '[data-collection-switch-link]',
};

const SECTION_ID_PREFIX = 'shopify-section-';

const instances = new WeakMap();

/**
 * @param {Element} element
 * @returns {element is HTMLElement}
 */
function isMainSectionElement(element) {
  if (!(element instanceof HTMLElement)) return false;
  return element.classList.contains('shopify-section') || element.id.startsWith(SECTION_ID_PREFIX);
}

/**
 * @param {ParentNode} root
 * @returns {HTMLElement[]}
 */
function getMainSections(root) {
  if (!(root instanceof Element)) return [];

  return Array.from(root.children).filter(
    /**
     * @param {Element} child
     * @returns {child is HTMLElement}
     */
    (child) => isMainSectionElement(child) && !!child.id
  );
}

/**
 * @param {unknown} error
 * @returns {boolean}
 */
function isAbortError(error) {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * Enhances collection icon scroller links to morph compatible MainContent
 * sections from the destination page without a full page reload.
 */
class CollectionSwitcher {
  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.root = root;
    this.mainContent = document.querySelector(SELECTORS.mainContent);
    this.requestId = 0;
    this.abortController = null;
  }

  init() {
    if (!(this.mainContent instanceof HTMLElement)) return;
    if (window.Shopify?.designMode) return;

    this.root.addEventListener('click', this.onClick);
    window.addEventListener('popstate', this.onPopState);
    this.syncCurrentState(new URL(window.location.href));
  }

  destroy() {
    this.root.removeEventListener('click', this.onClick);
    window.removeEventListener('popstate', this.onPopState);
    this.abortController?.abort();
    this.abortController = null;
  }

  onPopState = () => {
    this.renderFromURL(new URL(window.location.href), { pushHistory: false, sourceLink: null });
  };

  /**
   * @param {MouseEvent} event
   */
  onClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest(SELECTORS.link) : null;
    if (!(link instanceof HTMLAnchorElement) || !this.root.contains(link)) return;
    if (!this.shouldHandleClick(event, link)) return;

    const href = link.getAttribute('href');
    if (!href) return;

    const targetURL = new URL(href, window.location.origin);
    if (targetURL.origin !== window.location.origin) return;

    event.preventDefault();
    this.renderFromURL(targetURL, { pushHistory: true, sourceLink: link });
  };

  /**
   * @param {MouseEvent} event
   * @param {HTMLAnchorElement} link
   */
  shouldHandleClick(event, link) {
    if (event.defaultPrevented) return false;
    if (event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (link.target && link.target !== '_self') return false;
    if (link.hasAttribute('download')) return false;
    if (link.getAttribute('aria-disabled') === 'true') return false;
    return true;
  }

  /**
   * Renders target sections for the provided collection URL.
   *
   * @param {URL} url
   * @param {{ pushHistory: boolean; sourceLink: HTMLAnchorElement | null }} options
   */
  async renderFromURL(url, options) {
    const requestId = ++this.requestId;
    this.abortController?.abort();
    const abortController = new AbortController();
    this.abortController = abortController;

    this.root.setAttribute('aria-busy', 'true');

    try {
      const targetDocument = await this.fetchTargetDocument(url, abortController.signal);

      // Ignore stale response if a newer request started.
      if (requestId !== this.requestId) return;

      const morphTargets = this.getMorphTargets(targetDocument);
      if (!morphTargets.length) {
        window.location.href = url.toString();
        return;
      }

      for (const { currentSection, nextSection } of morphTargets) {
        morph(currentSection, nextSection, MORPH_OPTIONS);
      }

      if (targetDocument.title) {
        document.title = targetDocument.title;
      }

      if (options.pushHistory) {
        window.history.pushState({ collectionSwitcher: true }, '', url.toString());
      }

      this.syncCurrentState(url);

      if (options.sourceLink instanceof HTMLElement) {
        options.sourceLink.focus({ preventScroll: true });
      }
    } catch (error) {
      // Fall back to a regular navigation if morph rendering fails.
      if (requestId === this.requestId && !isAbortError(error)) {
        window.location.href = url.toString();
      }
    } finally {
      if (requestId === this.requestId) {
        this.abortController = null;
        this.root.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * Fetches and parses the target page document.
   *
   * @param {URL} url
   * @param {AbortSignal} signal
   * @returns {Promise<Document>}
   */
  async fetchTargetDocument(url, signal) {
    const response = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'same-origin',
      signal,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch target collection page: ${response.status}`);
    }

    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  }

  /**
   * Builds morph targets from the current and target page, excluding sections
   * that contain collection switchers so existing switcher instances stay alive.
   *
   * If the section structure/order changed, return an empty list to force a
   * full navigation fallback.
   *
   * @param {Document} targetDocument
   * @returns {Array<{ currentSection: HTMLElement; nextSection: HTMLElement }>}
   */
  getMorphTargets(targetDocument) {
    if (!(this.mainContent instanceof HTMLElement)) return [];

    const targetMainContent = targetDocument.querySelector(SELECTORS.mainContent);
    if (!(targetMainContent instanceof HTMLElement)) return [];

    const currentSections = getMainSections(this.mainContent);
    const nextSections = getMainSections(targetMainContent);

    if (!currentSections.length || !nextSections.length) return [];

    const currentById = new Map(currentSections.map((section) => [normalizeSectionId(section.id), section]));
    const nextById = new Map(nextSections.map((section) => [normalizeSectionId(section.id), section]));

    const switcherSectionIds = new Set(
      currentSections
        .filter((section) => !!section.querySelector(SELECTORS.switcher))
        .map((section) => normalizeSectionId(section.id))
    );

    const currentOrder = currentSections
      .map((section) => normalizeSectionId(section.id))
      .filter((id) => !switcherSectionIds.has(id));

    const nextOrder = nextSections
      .map((section) => normalizeSectionId(section.id))
      .filter((id) => !switcherSectionIds.has(id));

    if (currentOrder.length !== nextOrder.length) return [];

    for (let i = 0; i < currentOrder.length; i += 1) {
      if (currentOrder[i] !== nextOrder[i]) return [];
    }

    const morphTargets = [];

    for (const [sectionId, nextSection] of nextById) {
      if (switcherSectionIds.has(sectionId)) continue;

      const currentSection = currentById.get(sectionId);
      if (!currentSection) return [];

      morphTargets.push({ currentSection, nextSection });
    }

    return morphTargets;
  }

  /**
   * Syncs active/current state on scroller links from URL.
   *
   * @param {URL} url
   */
  syncCurrentState(url) {
    const handle = this.getCollectionHandle(url);

    this.root.querySelectorAll(SELECTORS.link).forEach((link) => {
      if (!(link instanceof HTMLElement)) return;
      const isCurrent = !!handle && link.dataset.collectionHandle === handle;

      link.classList.toggle('collection-icon-scroller__link--current', isCurrent);

      if (isCurrent) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

  /**
   * @param {URL} url
   * @returns {string}
   */
  getCollectionHandle(url) {
    const segments = url.pathname.split('/').filter(Boolean);
    const collectionsIndex = segments.indexOf('collections');
    if (collectionsIndex === -1) return '';
    return segments[collectionsIndex + 1] || '';
  }
}

/**
 * @param {ParentNode} [root=document]
 */
function initCollectionSwitchers(root = document) {
  root.querySelectorAll(SELECTORS.switcher).forEach((switcher) => {
    if (!(switcher instanceof HTMLElement) || instances.has(switcher)) return;

    const instance = new CollectionSwitcher(switcher);
    instance.init();
    instances.set(switcher, instance);
  });
}

/**
 * @param {ParentNode} [root=document]
 */
function teardownCollectionSwitchers(root = document) {
  root.querySelectorAll(SELECTORS.switcher).forEach((switcher) => {
    if (!(switcher instanceof HTMLElement)) return;

    const instance = instances.get(switcher);
    if (!instance) return;

    instance.destroy();
    instances.delete(switcher);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initCollectionSwitchers());
} else {
  initCollectionSwitchers();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) {
    initCollectionSwitchers(event.target);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  if (event.target instanceof HTMLElement) {
    teardownCollectionSwitchers(event.target);
  }
});